// Phase JB.3c — useFounderOffer hook.
//
// Auth-only. Returns the user's own founder_offers row (if any),
// the live globalRemaining count via founder_offers_remaining() RPC,
// and a claim() function that POSTs to jb-founder-offer-claim.
//
// N06 honesty: globalRemaining is the real RPC value — never fudged.

import { useCallback, useEffect, useId, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { useComposedQuery } from "../data/useTableQuery.ts";

export interface FounderOffer {
  offer_id: string;
  user_id: string;
  started_at: string;
  expires_at: string;
  status: "offered" | "active" | "accepted" | "redeemed" | "declined" | "expired";
  discount_pct: number;
}

export interface UseFounderOfferResult {
  offer: FounderOffer | null;
  globalRemaining: number | null;
  loading: boolean;
  claiming: boolean;
  error: string | null;
  claim: () => Promise<void>;
}

/** Statuses that mean the user already has an offer (cannot claim again). */
export function offerIsActive(o: FounderOffer | null): boolean {
  if (!o) return false;
  return o.status === "offered" || o.status === "active" ||
    o.status === "accepted" || o.status === "redeemed";
}

interface FounderOfferComposite {
  globalRemaining: number | null;
  offer: FounderOffer | null;
  /** RPC / table error message, surfaced with the same semantics as before. */
  errorMessage: string | null;
}

export function useFounderOffer(): UseFounderOfferResult {
  const { user, session } = useAuthSession();
  const instanceId = useId();
  // Local override: realtime payloads + claim() set this for instant offer
  // updates; it takes precedence over the query's offer until the next refetch
  // resolves. Preserves the old optimistic setOffer() behavior.
  const [offerOverride, setOfferOverride] = useState<FounderOffer | null>(null);
  const [overrideSet, setOverrideSet] = useState<boolean>(false);
  const [claiming, setClaiming] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const userId = user?.id ?? null;

  const q = useComposedQuery<FounderOfferComposite>(
    ["founder-offer", userId],
    async (sb) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: rpcData, error: rpcErr } = await (sb as any).rpc("founder_offers_remaining");
      let globalRemaining: number | null = null;
      let errorMessage: string | null = null;
      if (rpcErr) {
        errorMessage = rpcErr.message;
      } else if (typeof rpcData === "number") {
        globalRemaining = rpcData;
      } else if (Array.isArray(rpcData) && rpcData.length > 0 && typeof rpcData[0] === "number") {
        globalRemaining = rpcData[0];
      }

      let offer: FounderOffer | null = null;
      if (userId) {
        const res = await (
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (sb.from("founder_offers" as never) as any)
            .select("offer_id,user_id,started_at,expires_at,status,discount_pct")
            .eq("user_id", userId)
            .order("started_at", { ascending: false })
            .limit(1)
            .maybeSingle()
        ) as { data: FounderOffer | null; error: { message: string } | null };
        if (res.error) errorMessage = res.error.message;
        else offer = res.data ?? null;
      }

      return { globalRemaining, offer, errorMessage };
    },
  );

  // Surface RPC/table errors with the same semantics as the old imperative code.
  useEffect(() => {
    if (q.data?.errorMessage) setError(q.data.errorMessage);
  }, [q.data?.errorMessage]);

  const refetch = q.refetch;
  const refreshRemaining = useCallback(async (): Promise<void> => {
    await refetch();
  }, [refetch]);

  const globalRemaining = q.data?.globalRemaining ?? null;
  const queryOffer = q.data?.offer ?? null;
  const offer = overrideSet ? offerOverride : queryOffer;
  const loading = q.isLoading;

  // Once a fresh query result lands, drop the optimistic override so the query
  // becomes the source of truth again.
  useEffect(() => {
    if (!q.isFetching) setOverrideSet(false);
  }, [q.data, q.isFetching]);

  // Realtime channel — unchanged behavior: optimistic setOffer + refetch.
  useEffect(() => {
    if (!user) {
      setOfferOverride(null);
      setOverrideSet(false);
      return;
    }
    const sb = getBrowserClient();
    const channel = sb
      .channel(`founder_offers:${user.id}:${instanceId}`)
      .on(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        "postgres_changes" as any,
        { event: "*", schema: "public", table: "founder_offers", filter: `user_id=eq.${user.id}` },
        (payload: { new: FounderOffer }) => {
          setOfferOverride(payload.new);
          setOverrideSet(true);
          void refetch();
        },
      )
      .subscribe();

    return () => {
      void sb.removeChannel(channel);
    };
  }, [user, instanceId, refetch]);

  const claim = useCallback(async (): Promise<void> => {
    const accessToken = session?.access_token;
    if (!accessToken) {
      setError("auth_required");
      return;
    }
    const url = import.meta.env.VITE_SUPABASE_URL;
    if (!url) {
      setError("config_error");
      return;
    }
    setClaiming(true);
    setError(null);
    try {
      const res = await fetch(`${url}/functions/v1/jb-founder-offer-claim`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: "{}",
      });
      const body = (await res.json()) as
        | { ok: true; offer: FounderOffer }
        | { ok: false; reason: string };
      if (!res.ok || body.ok !== true) {
        const reason = "reason" in body ? body.reason : `http_${res.status}`;
        setError(reason);
        // Refresh remaining — cap may have changed.
        await refreshRemaining();
        return;
      }
      setOfferOverride(body.offer);
      setOverrideSet(true);
      await refreshRemaining();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setClaiming(false);
    }
  }, [session?.access_token, refreshRemaining]);

  return { offer, globalRemaining, loading, claiming, error, claim };
}
